import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

interface MaintenanceGuardProps {
  children: React.ReactNode;
}

export function MaintenanceGuard({ children }: MaintenanceGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isLoading: roleLoading } = useUserRole();
  const [shouldRender, setShouldRender] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  const { data: isMaintenanceMode, isLoading: maintenanceLoading, error } = useQuery({
    queryKey: ['maintenance-mode'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.rpc('is_feature_enabled', {
          p_flag_key: 'maintenance_mode'
        });
        
        if (error) {
          console.error('Maintenance check error:', error);
          return false; // Default to not in maintenance on error
        }
        
        return data as boolean;
      } catch (err) {
        console.error('Maintenance check exception:', err);
        return false; // Default to not in maintenance on exception
      }
    },
    refetchInterval: 30000,
    retry: 2, // Only retry twice
    staleTime: 60000, // Cache for 1 minute
  });

  // Timeout fallback - force render after 1 second
  useEffect(() => {
    const timer = setTimeout(() => {
      setTimedOut(true);
      setShouldRender(true);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Handle errors by defaulting to not in maintenance
  useEffect(() => {
    if (error) {
      console.error('Maintenance mode query error, assuming not in maintenance');
      setShouldRender(true);
    }
  }, [error]);

  useEffect(() => {
    if (maintenanceLoading || roleLoading) {
      return;
    }

    // If maintenance mode is OFF, render normally
    if (!isMaintenanceMode) {
      setShouldRender(true);
      return;
    }

    // If maintenance mode is ON
    if (isAdmin) {
      // Admins can access /admin routes only
      if (!location.pathname.startsWith('/admin')) {
        navigate('/admin');
      } else {
        setShouldRender(true);
      }
    } else {
      // Regular users redirected to maintenance page
      if (location.pathname !== '/maintenance' && location.pathname !== '/auth') {
        navigate('/maintenance');
      } else {
        setShouldRender(true);
      }
    }
  }, [isMaintenanceMode, isAdmin, roleLoading, maintenanceLoading, location.pathname, navigate]);

  // Show loading spinner while checking (with timeout)
  if ((maintenanceLoading || roleLoading || !shouldRender) && !timedOut) {
    return (
      <div className="min-h-screen-dvh flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show maintenance banner for admins
  if (isMaintenanceMode && isAdmin) {
    return (
      <>
        <Alert variant="destructive" className="rounded-none border-x-0 border-t-0">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="font-semibold">
            🔧 MAINTENANCE MODE ACTIVE - Regular users cannot access the platform. Only admins can access /admin routes.
          </AlertDescription>
        </Alert>
        {children}
      </>
    );
  }

  return <>{children}</>;
}
